// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  IAboutUsStructureGetAllAttrs,
  IAboutUsStructureGetAttrs,
  IAboutUsStructureSaveAttrs,
} from 'interfaces/back-office/content-management/about-us/structure/about-us-structure-view.interface'
import {
  IAboutUsStructureApiGetAllResponse,
  IAboutUsStructureApiGetResponse,
  IAboutUsStructureApiSaveResponse,
} from 'interfaces/back-office/content-management/about-us/structure/about-us-structure-api.interface'
import { IAboutUsStructure } from 'interfaces/back-office/content-management/about-us/structure'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'

// About Us Structure Props
const aboutUsStructureProps = {
  id: types.maybeNull(types.number),
  number: types.maybeNull(types.number),
  content: types.maybeNull(types.string),
  imageURL: types.maybeNull(types.string),
  originalFileName: types.maybeNull(types.string),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

const AboutUsStructures = types.array(
  types.model('AboutUsStructures').props({
    ...aboutUsStructureProps,
    aboutUsStructures: types.maybeNull(types.array(types.model('AboutUsStructures').props(aboutUsStructureProps))),
  }),
)

const AboutUsStructure = types.model('aboutUsStructure').props(aboutUsStructureProps)

const AboutUsStructureStoreModel = types
  .model('aboutUsStructure')
  .props({
    aboutUsStructure_isLoading: types.optional(types.boolean, false),
    aboutUsStructure_isSaveLoading: types.optional(types.boolean, false),
    aboutUsStructure_aboutUsStructures: types.optional(AboutUsStructures, []),
    aboutUsStructure_aboutUsStructure: types.optional(AboutUsStructure, {}),
  })
  .actions(self => ({
    aboutUsStructure_SET_LOADING: (payload: boolean): void => {
      self.aboutUsStructure_isLoading = payload
    },
    aboutUsStructure_SET_SAVE_LOADING: (payload: boolean): void => {
      self.aboutUsStructure_isSaveLoading = payload
    },
    aboutUsStructure_SET_ABOUT_US_STRUCTURES: (payload: IAboutUsStructure[]): void => {
      self.aboutUsStructure_aboutUsStructures = cast(payload?.map((list, index) => ({ ...list, number: index + 1 })) || [])
    },
    aboutUsStructure_SET_ABOUT_US_STRUCTURE: (payload: IAboutUsStructure): void => {
      self.aboutUsStructure_aboutUsStructure = cast(payload)
    },
    aboutUsStructure_CLEAR_ABOUT_US_STRUCTURES: (): void => {
      self.aboutUsStructure_aboutUsStructures = cast([])
    },
    aboutUsStructure_CLEAR_ABOUT_US_STRUCTURE: (): void => {
      self.aboutUsStructure_aboutUsStructure = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch about us structures
     *
     * @param {IAboutUsStructureGetAttrs} payload
     *
     * @return {any} any
     */
    aboutUsStructure_fetchAboutUsStructures: flow(function* (payload: IAboutUsStructureGetAllAttrs) {
      self.aboutUsStructure_SET_LOADING(true)

      const response: IAboutUsStructureApiGetAllResponse =
        yield self.environment.aboutUsStructureApi.aboutUsStructure_getAboutUsStructures({
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        self.aboutUsStructure_SET_ABOUT_US_STRUCTURES(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.aboutUsStructure_SET_LOADING(false)
    }),

    /**
     * @description Fetch about us structures
     *
     * @param {IAboutUsStructureGetAttrs} payload
     *
     * @return {any} any
     */
    aboutUsStructure_fetchAboutUsStructure: flow(function* (payload: IAboutUsStructureGetAttrs) {
      self.aboutUsStructure_SET_LOADING(true)

      const response: IAboutUsStructureApiGetResponse =
        yield self.environment.aboutUsStructureApi.aboutUsStructure_getAboutUsStructure({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        self.aboutUsStructure_SET_ABOUT_US_STRUCTURE(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.aboutUsStructure_SET_LOADING(false)
    }),

    /**
     * @description Save about us structure
     *
     * @param {IAboutUsStructureSaveAttrs} payload
     *
     * @return {any} any
     */
    aboutUsStructure_saveAboutUsStructure: flow(function* (payload: IAboutUsStructureSaveAttrs) {
      self.aboutUsStructure_SET_SAVE_LOADING(true)

      const response: IAboutUsStructureApiSaveResponse =
        yield self.environment.aboutUsStructureApi.aboutUsStructure_saveAboutUsStructure({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.aboutUsStructure_SET_SAVE_LOADING(false)
    }),
  }))

export const createAboutUsStructureStoreDefaultModel = () => types.optional(AboutUsStructureStoreModel, {})
